import { inject, Injectable } from '@angular/core';
import {
  AuthSession,
  confirmSignIn,
  fetchAuthSession,
  setUpTOTP,
  SetUpTOTPOutput,
  signIn,
  signOut,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectUserToken } from '../authentication/state/selector';
import { tokenRefreshSuccess } from '../authentication/state/actions/authentication.actions';
import { confirmResetPassword, resetPassword, signInWithRedirect } from '@aws-amplify/auth';
import { TenantService } from '../authentication/service/tenantService';

@Injectable({ providedIn: 'root' })
export class AmplifyService {
  private readonly store = inject(Store);
  private readonly tenantService = inject(TenantService);

  constructor() {
    cognitoUserPoolsTokenProvider.setClientMetadataProvider(() =>
      Promise.resolve({ tenant: this.tenantService.tenant })
    );
  }

  private buildClientMetadataOptions() {
    return {
      clientMetadata: {
        tenant: this.tenantService.tenant,
      },
    };
  }

  async signIn(username: string, password: string): Promise<any> {
    return signIn({
      username: username?.toLowerCase() ?? '',
      password: password,
      options: this.buildClientMetadataOptions(),
    });
  }

  async signInWithSSO(): Promise<void> {
    const tenant = this.tenantService.tenant;
    if (this.tenantService.isSsoEnabled()) {
      await signInWithRedirect({
        provider: { custom: tenant },
      });
    } else {
      throw new Error(`SSO is not enabled for tenant: ${tenant}`);
    }
  }

  async signOut(): Promise<void> {
    await signOut();
  }

  async fetchSession(): Promise<AuthSession> {
    return fetchAuthSession();
  }

  // Token refresh on 401 in interceptor
  async fetchSessionForceRefresh(): Promise<AuthSession> {
    return fetchAuthSession({ forceRefresh: true });
  }

  public async getToken(): Promise<string | null> {
    return await firstValueFrom(this.store.select(selectUserToken));
  }

  // Checks Cognito directly instead of just store token to prevent stale session issues
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      const newToken = session?.tokens?.idToken?.toString();

      if (!newToken) {
        return false;
      }

      const oldToken = await this.getToken();
      if (oldToken !== newToken) {
        this.store.dispatch(tokenRefreshSuccess({ token: newToken }));
      }

      return true;
    } catch (error) {
      console.error('Session fetch failed:', error);
      return false;
    }
  }

  async resetPassword(email: string) {
    return await resetPassword({
      username: email.toLowerCase(),
    });
  }

  async confirmResetPassword(email: string, confirmationCode: string, newPassword: string) {
    return await confirmResetPassword({
      username: email.toLowerCase(),
      confirmationCode: confirmationCode,
      newPassword: newPassword,
    });
  }

  async confirmSignIn(newPassword: string) {
    return await confirmSignIn({
      challengeResponse: newPassword,
      options: this.buildClientMetadataOptions(),
    });
  }

  /** Existing tokens stay valid, so the user remains logged in. */
  async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    await updatePassword({ oldPassword, newPassword });
  }

  /** True when the current session came from a federated (SSO) identity provider. */
  async isFederatedSignIn(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      return !!session?.tokens?.idToken?.payload?.['identities'];
    } catch {
      return false;
    }
  }

  /** Starts TOTP enrollment for the current user and returns the setup details (shared secret + QR URI builder). */
  async setupTotp(): Promise<SetUpTOTPOutput> {
    return setUpTOTP();
  }

  /** Confirms the code from the authenticator app and makes TOTP the preferred MFA method for future logins. */
  async verifyTotpSetup(code: string): Promise<void> {
    await verifyTOTPSetup({ code });
    await updateMFAPreference({ totp: 'PREFERRED' });
  }

  /** Responds to the CONFIRM_SIGN_IN_WITH_TOTP_CODE challenge during sign-in. */
  async confirmMfaSignIn(code: string) {
    return confirmSignIn({
      challengeResponse: code,
      options: this.buildClientMetadataOptions(),
    });
  }
}
